import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, PageTemplate } from "components";
import { useParams } from "react-router-dom";
import styles from "./style.module.scss";
import { financialDemanExcel, getDemandDetail } from "Services/Reporting";
import { v4 as uuidv4 } from "uuid";
import AccordionTable from "./components/AccordionTable";
import { ResponseHandler, Table } from "components";
import { addCamma } from "utils/helpers";
import ExtendRowTable from "components/ExtendRowTable/ExtendRowTable";
import { ReactComponent as InfoIcon } from "assets/icons/info.svg";
import { ReactComponent as Download } from "assets/icons/cloud-download.svg";
import { demandObj } from "./types";
import { convertDate, getObjectOfTable } from "./utils";

const Demand = () => {
    const { vendorID, periodID } = useParams<{
        vendorID: string;
        periodID: string;
    }>();

    const [demandReport, setDemandReport] = useState({
        b2b: [],
        b2c: [],
        offline: [],
        total: [],
        summary: [],
    });
    const { isFetching, isError } = useQuery(
        ["financialSale", periodID, vendorID],
        () => getDemandDetail({ periodID, vendorID }),
        {
            onSuccess: (res) => {
                setDemandReport(res?.data);
            },
        }
    );

    const { mutate: mutateExportExcel } = useMutation(
        ["financial-demand-excel"],
        financialDemanExcel,
        {
            onSuccess: (res) => {
                const link = document.createElement("a");
                link.href = res.data;
                link.setAttribute("download", "financial-demand-excel");
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
            },
        }
    );

    const b2cData: any = demandReport?.b2c;
    const b2bData: any = demandReport?.b2b;
    const offlineData: any = demandReport?.offline;
    const sumData: any = demandReport?.total?.map((item: any) => {
        return {
            ...item,
            commissions: "",
        };
    });
    const summaryData: any = demandReport?.summary;

    const data = [
        { sales_channel: "b2c", id: uuidv4(), ...summaryData?.b2c },
        { sales_channel: "b2b", id: uuidv4(), ...summaryData?.b2b },
        { sales_channel: "offline", id: uuidv4(), ...summaryData?.offline },
        { sales_channel: "total", id: uuidv4(), ...summaryData?.total },
    ];

    const demandReports = [
        {
            id: 0,
            title: "بستر فروش اسنپ‌فود",
            active: false,
            data: b2cData,
        },
        {
            id: 1,
            title: "بستر فروش سازمانی",
            active: false,
            data: b2bData,
        },
        {
            id: 2,
            title: "بستر فروش آفلاین",
            active: false,
            data: offlineData,
        },
        {
            id: 3,
            title: "مجموع",
            active: false,
            data: sumData,
        },
    ];

    const terms: any = {
        b2c: "اسنپ‌فود",
        b2b: "سازمانی",
        offline: "آفلاین",
        total: "مجموع",
    };

    const SumTableColumns = [
        {
            Header: "بستر فروش",
            accessor: "sales_channel",
            Cell: (props: any) => terms[props.row.original?.sales_channel],
        },
        {
            Header: "شامل کمیسیون",
            accessor: "total_with_calc_sales_quantity",
        },
        {
            Header: "شامل کمیسیون (ریال)",
            accessor: "total_with_calc_sales_amount",
            Cell: (props: any) => <span>{addCamma(props.value)}</span>,
        },
        {
            Header: "فی (ریال)",
            accessor: "dispatch_fee",
            Cell: (props: any) => <span>{addCamma(props.value)}</span>,
        },
        {
            Header: "کمیسیون (ریال)",
            accessor: "commission",
            Cell: (props: any) => <span>{addCamma(props.value)}</span>,
        },
        {
            Header: "سهم کیچن (ریال)",
            accessor: "kitchen_share",
            Cell: (props: any) => <span>{addCamma(props.value)}</span>,
        },
        {
            Header: "تامین شده توسط کیچن",
            accessor: "total_no_calc_with_supplied_sales_quantity",
        },
        {
            Header: " تامین شده توسط کیچن (ریال)",
            accessor: "total_no_calc_with_supplied_sales_amount",
            Cell: (props: any) => <span>{addCamma(props.value)}</span>,
        },
        {
            Header: "برداشت‌ها (ریال)",
            accessor: "withdrawl_amount",
            Cell: (props: any) => <span>{addCamma(props.value)}</span>,
        },
    ];

    const COLUMN = [
        {
            Header: "شعبه",
            accessor: "branch_title",
            enableRowSpan: true,
        },
        {
            Header: "شامل کمیسیون",
            accessor: "total_with_calc_sales_quantity",
        },
        {
            Header: "شامل کمیسیون (ریال)",
            accessor: "total_with_calc_sales_amount",
            Cell: (props: any) => addCamma(props.value),
        },
        {
            Header: "فی (ریال)",
            accessor: "dispatch_fees",
            Cell: (props: any) => (
                <div className={styles.multiColumns}>
                    {props.value?.map((item: any) => (
                        <div className={styles.handleTooltip}>
                            <span>{item.amount}</span>
                            <InfoIcon className={styles.infoIcon} />
                            <span>{convertDate(item?.date_range)}</span>
                        </div>
                    ))}
                </div>
            ),
        },
        {
            Header: "کمیسیون (درصد)",
            accessor: "commissions",
            Cell: (props: any) => (
                <div className={styles.multiColumns}>
                    {props.value ? (
                        props.value?.map((item: any) => (
                            <div className={styles.handleTooltip}>
                                <span>{item?.comission}</span>
                                <InfoIcon className={styles.infoIcon} />
                                <span>{convertDate(item?.date_range)}</span>
                            </div>
                        ))
                    ) : (
                        <div className={styles.dashStyle}>-</div>
                    )}
                </div>
            ),
        },
        {
            Header: "سهم کیچن (ریال)",
            accessor: "kitchen_share",
            Cell: (props: any) => addCamma(props.value),
        },
        {
            Header: "تامین شده توسط کیچن",
            accessor: "total_no_calc_with_supplied_sales_quantity",
        },
        {
            Header: "تامین شده توسط کیچن (ریال)",
            accessor: "total_no_calc_with_supplied_sales_amount",
            Cell: (props: any) => addCamma(props.value),
        },
        {
            Header: "برداشت‌ها (ریال)",
            accessor: "withdrawl_amount",
            Cell: (props: any) => addCamma(props.value),
        },
    ];

    return (
        <>
            <ResponseHandler loading={isFetching}>
                <PageTemplate
                    groupName="گزارشات"
                    title={"دوره مالی"}
                    listOfTitles={["مطالبات کیچن"]}
                    customStylePageTemplate={styles.customStylePageTemplate}
                >
                    <>
                        <div className={styles.exportExcalBtn}>
                            <Button
                                btnText={"خروجی اکسل"}
                                className="is-green bordered half-height"
                                handleClick={() =>
                                    mutateExportExcel({ periodID, vendorID })
                                }
                                btnIcon={<Download className="mr-2" />}
                            />
                        </div>
                        <>
                            <Table
                                columns={SumTableColumns}
                                data={data || []}
                                className={`${styles.sumFinancialTable} ${styles.demandMargin}`}
                            />
                            {demandReports.map((report) => {
                                return (
                                    <AccordionTable
                                        report={report}
                                        key={report.id}
                                        tableComponent={
                                            <>
                                                <div
                                                    className={
                                                        styles.tableContainer
                                                    }
                                                >
                                                    <div
                                                        className={
                                                            styles.tableContent
                                                        }
                                                    >
                                                        <ExtendRowTable
                                                            className={
                                                                styles.financialTable
                                                            }
                                                            columns={COLUMN}
                                                            data={report?.data}
                                                            customTableStyle={
                                                                styles.customTableTd
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        }
                                    />
                                );
                            })}
                        </>
                    </>
                </PageTemplate>
            </ResponseHandler>
        </>
    );
};

export default Demand;
